import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// The worker is handed to react-pdf as a live object rather than a URL.
// Pointing GlobalWorkerOptions at a .mjs URL breaks once deployed: the host
// answers a missing file with index.html, pdf.js cannot parse it and quietly
// falls back to the fake worker ("Setting up fake worker"), which then fails
// to render. One worker is created for the life of the tab, because building
// a fresh PDFWorker from the same port throws once the first is destroyed.
let documentOptions = null;

function getDocumentOptions() {
  if (!documentOptions) {
    const port = new Worker(
      new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
      { type: "module" },
    );

    documentOptions = { worker: new pdfjs.PDFWorker({ port }) };
  }

  return documentOptions;
}

function PreviewPage({
  number,
  width,
  ratio,
  scrollRoot,
  onRenderError,
  keepDrawn,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(number === 1);
  // The width whose drawing is finished. With `keepDrawn`, that drawing stays
  // on screen, stretched to fit, until the new width is drawn, so resizing
  // never blanks the page.
  const [drawn, setDrawn] = useState(width);
  const widths = keepDrawn && drawn !== width ? [drawn, width] : [width];

  // A page that is off screen has no drawing to keep.
  useEffect(() => {
    if (!visible) setDrawn(width);
  }, [visible, width]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      {
        root: scrollRoot.current,
        rootMargin: "800px 0px",
      },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [scrollRoot]);

  return (
    <div
      ref={ref}
      className="agreement-pdf-page"
      data-page-number={number}
      style={{
        width,
        height: width * ratio,
      }}
    >
      {visible &&
        widths.map((size) => (
          <div
            key={size}
            className="agreement-pdf-drawing"
            style={
              widths.length === 1
                ? undefined
                : size === width
                  ? { visibility: "hidden" }
                  : { transform: `scale(${width / size})` }
            }
          >
            <Page
              pageNumber={number}
              width={size}
              devicePixelRatio={Math.min(window.devicePixelRatio || 1, 2)}
              onRenderSuccess={() => setDrawn(size)}
              onRenderError={onRenderError}
              onLoadError={onRenderError}
              loading={<p>Loading page {number}...</p>}
            />
          </div>
        ))}
    </div>
  );
}

export default function AgreementPreview({
  url,
  width,
  scrollRoot,
  onReady,
  onError,
  loadingLabel = "Loading agreement...",
  keepDrawn = false,
}) {
  const [ratios, setRatios] = useState([]);
  const active = useRef(true);

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  const loaded = async (document) => {
    try {
      const dimensions = await Promise.all(
        Array.from({ length: document.numPages }, async (_, index) => {
          const page = await document.getPage(index + 1);
          const viewport = page.getViewport({ scale: 1 });

          return viewport.height / viewport.width;
        }),
      );

      if (!active.current) return;

      setRatios(dimensions);
      onReady(document.numPages);
    } catch (error) {
      if (active.current) {
        onError(error);
      }
    }
  };

  return (
    <Document
      file={url}
      options={getDocumentOptions()}
      onLoadSuccess={loaded}
      onLoadError={onError}
      loading={<p>{loadingLabel}</p>}
    >
      {ratios.map((ratio, index) => (
        <PreviewPage
          key={index + 1}
          number={index + 1}
          width={width}
          ratio={ratio}
          scrollRoot={scrollRoot}
          onRenderError={onError}
          keepDrawn={keepDrawn}
        />
      ))}
    </Document>
  );
}
